import { Controller } from "@hotwired/stimulus"
import { useDebounce, useClickOutside } from "stimulus-use"
import { get } from "@rails/request.js"

// Connects to data-controller="autocomplete"
export default class extends Controller {
  static targets = ["input", "actual", "list", "optTpl", "status", "btn"]
  static debounces = ["search"]
  static values = {
    url: String,
    minChars: { type: Number, default: 2 }
  }

  connect() {
    useDebounce(this, { wait: 200 })
    useClickOutside(this)

    this.toggleBtn()
  }

  clickOutside(event) {
    if (this.listTarget.contains(event.target)) { return }

    this.hidePopover()
  }

  input(event) {
    if (event.key == "Enter") {
      event.preventDefault()
    }

    this.search()
  }

  focus(event) {
    if (this.hasListItems) {
      this.showPopover()
    }
  }

  select(event) {
    const option = event.target.closest('[data-value]')
    if (!option) return

    const id = option.dataset.value
    const name = option.dataset.label

    this.actualTarget.value = id
    this.inputTarget.value = name

    // Close popover after selection
    this.hidePopover()

    // Dispatch change event
    this.actualTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  async search() {
    const query = this.inputTarget.value

    if (query.length < this.minCharsValue) {
      this.hidePopover()
      return
    }

    this.showStatus()

    const response = await get(this.urlValue, {
      query: { query: query },
      responseKind: "json"
    })

    this.hideStatus()

    if (response.ok) {
      const results = await response.json
      this.renderResults(results)
    }
  }

  renderResults(results) {
    this.listTarget.innerHTML = ""

    if (results.length === 0) {
      this.renderNoResults()
      return
    }

    results.forEach(result => {
      const option = this.optTplTarget.content.cloneNode(true).firstElementChild
      option.id = `campaign-${result.id}-option`
      option.dataset.value = result.id
      option.dataset.label = result.name
      option.textContent = result.name

      this.listTarget.appendChild(option)
    })

    // Show the btn if there are results
    this.toggleBtn()

    // Show popover after rendering results
    this.showPopover()
  }

  renderNoResults() {
    const option = document.createElement("div")
    option.className = "block px-3 py-2 text-slate-500 text-sm select-none"
    option.textContent = `No results found for ${this.inputTarget.value}`
    this.listTarget.appendChild(option)
    this.hideBtn()
  }

  showPopover() {
    if (this.listTarget.showPopover) {
      this.listTarget.showPopover()
    }
  }

  hidePopover() {
    if (this.listTarget.hidePopover) {
      this.listTarget.hidePopover()
    }
  }

  showStatus() {
    this.statusTarget.classList.remove('opacity-0')
  }

  hideStatus() {
    this.statusTarget.classList.add('opacity-0')
  }

  toggleBtn() {
    if (!this.hasBtnTarget) { return }

    if (this.hasListItems) {
      this.showBtn()
    } else {
      this.hideBtn()
    }
  }

  showBtn() { this.btnTarget.classList.remove('hidden') }
  hideBtn() { this.btnTarget.classList.add('hidden') }

  get hasListItems() {
    if (!this.hasListTarget) { return false }

    const items = this.listTarget.querySelectorAll('li')
    return items.length > 0
  }
}
